import { useState, useEffect, useCallback } from 'react';
import { Room, LocalAudioTrack } from 'twilio-video';

export const useAudioDevices = (room: Room | undefined) => {
  const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>([]);
  const [activeMicId, setActiveMicId] = useState<string>();

  const load = useCallback(async () => {
    await navigator.mediaDevices.getUserMedia({ audio: true }).catch(() => {});
    const devices = await navigator.mediaDevices.enumerateDevices();
    const mics = devices.filter((d) => d.kind === 'audioinput');
    setMicrophones(mics);

    const currentTrack = room
      ? Array.from(room.localParticipant.audioTracks.values())[0]?.track
      : undefined;
    if (currentTrack) {
      setActiveMicId(currentTrack.mediaStreamTrack.getSettings().deviceId);
    } else if (mics.length > 0) {
      setActiveMicId(mics[0].deviceId);
    }
  }, [room]);

  const switchMicrophone = useCallback(
    async (deviceId: string) => {
      if (!room) return;

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: deviceId } },
      });

      const newTrack = new LocalAudioTrack(stream.getAudioTracks()[0]);

      const publications = Array.from(room.localParticipant.audioTracks.values());
      for (const pub of publications) {
        pub.track.stop();
        room.localParticipant.unpublishTrack(pub.track);
      }

      await room.localParticipant.publishTrack(newTrack);
      setActiveMicId(deviceId);
    },
    [room]
  );

  useEffect(() => {
    load();
    navigator.mediaDevices.addEventListener('devicechange', load);
    return () => navigator.mediaDevices.removeEventListener('devicechange', load);
  }, [room]);

  return { microphones, activeMicId, switchMicrophone };
};
